import { autoinject, transient, observable, computedFrom, BindingEngine } from "aurelia-framework";
import { DataType, QueryFilter, DataGridColumn, SourceType } from "../../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import * as moment from "moment";
import { LookupValue, LookupValueRequest, LookupValueResponse } from "../../../../../models/lookups/lookup.models";
import { MobileHelper } from "../../../../../helpers/mobile-helper";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import * as Enumerable from "linq";
import { Router } from "aurelia-router";
import { BaseActivity } from "./../base-activity";
import { TasksConstants } from "../../../../../models/tasks/task.model";
import KeyDownEvent = JQuery.KeyDownEvent;
import { Guid } from "../../../../../utilities/guid";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../../services/basics/signal-functions";
import { SignalClientService } from '@rightship-tech/common-ui/services/basic/signal-client.service';
import { UserClientService } from "../../../../../services/authentication/user-client.service";
import { UserClaimsService } from "../../../../../services/authentication/user-claims.service";
import { QueriesClientService } from "../../../../../services/queries/queries-client.services";
import { Claims } from "../../../../../constants/authentication/claims";
import { Claims as CommonUiClaims } from "@rightship-tech/common-ui/constants/authentication/claims";
import { CfrStatusEnum } from "../../../../../models/cfr/cfr.models";
import { RightshipDataGridColumn } from "../../../../components/rightship-data-grid/rightship-data-grid";
import { CustomerService as CommonCustomerService } from "@rightship-tech/common-ui/services/basic/customer-client.service";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Reflection } from "../../../../../utilities/reflection";
import { cachable } from '@rightship-tech/common-ui/decorators/cachable';
import { EntityTypeConfigurationModel } from "@rightship-tech/common-ui/components/comp-entity-finder/comp-entity-finder.models";
import { EntityTypeConfigurationHelper } from "@rightship-tech/common-ui/helpers/entity-type-configuration.helper";

@autoinject()
@transient()
export class CfrActivities extends BaseActivity {

    private _isReloading: boolean;
    
    public companiesEntityTypes: EntityTypeConfigurationModel[];

    getColumns(): DataGridColumn[] {
        return [
            {
                dataType: DataType.CustomTemplate,
                value: '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<a class="link" href.bind="getItemRoute(item.cfrId)" mousedown.trigger="itemClick()">${item.vesselName}</a>' +
                    '<svg class="rs-badge" viewBox="0 0 30 30" if.bind="item.isRightShipMember && isRightShipUser">' +
                    '<g>' +
                    '<path fill="#003745" d="M2,0h26c1.1,0,2,0.9,2,2v26c0,1.1-0.9,2-2,2H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '<path fill="#FF5F1B" d="M2,0h1.5c0.5,0,1,0.2,1.4,0.6l15.5,14.8l0,0L4.9,29.5C4.5,29.8,4,30,3.5,30H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '</g>' +
                    '</svg>' +
                    '</div>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span></div>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span></div>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span></div>',
                name: "vesselName",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Name",
                mobileTitle: "Name",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    getItemRoute: this.getItemRoute.bind(this),
                    itemClick: this.itemClick.bind(this),
                    isRightShipUser: this.rightShipUser
                }
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 1,
                dataType: DataType.DateTime,
                name: 'submittedDate',
                title: 'Date Occurred',
                isSortable: true,
                valueConverter: (date: Date | string) => date
                    ? moment(date).format('DD MMM YYYY HH:mm')
                    : '',
            },
            <RightshipDataGridColumn>{
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                name: 'information',
                title: 'Information',
                ellipsis: 75,
                isSortable: false,
                disableSorting: true,
            },
            <RightshipDataGridColumn>{
                columnOrder: 3,
                sortOrder: 3,
                dataType: DataType.Default,
                name: 'vettingApplicability',
                title: 'Vetting Applicability',
                ellipsis: 75,
                isSortable: true,
            },
            <DataGridColumn>{
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'requesterCustomer',
                title: 'Reporter/ CFR ID',
                value:
                    '<div class="reviewer">' +
                    '<span class="name" if.bind="item.requester">${item.requesterCustomer} - ${item.requester}</span>' +
                    '<span class="type" if.bind="item.cfrId">CFR ID #${item.cfrId}</span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'reviewer',
                title: 'Reviewer',
                value:
                    '<div class="reviewer" if.bind="item.reviewer">' +
                    '<span class="name">${item.reviewer}</span>' +
                    '<span class="type" if.bind="item.reviewerType">${item.reviewerType}</span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'status',
                title: 'Status',
                value:
                    '<div class="status-badge-value" if.bind="item.status">' +
                    '<div class="row nowrap align-items-middle">' +
                    '<div class="status-badge-dot"></div>' +
                    '<span>${item.status}</span>' +
                    '<div class="info" if.bind="item.awaitingResponseStatusReason && item.status.toLowerCase() != \'unacceptable\' && item.status.toLowerCase() != \'acceptable\'">' +
                    '<i class="icon-info material-icons">info_outline</i>' +

                    //Tooltip
                    '<div class="status-tooltip">' +
                    '<div class="wrap-tooltip">' +
                    '<p class="desc">${item.awaitingResponseStatusReason}</p>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '<span class="status-date">${getDateToString(item.statusDate)}</span>' +
                    '</div>',
                isSortable: true,
                getCellClass: (item: { status: string }) => {
                    return (item && item.status) ? `status-badge ${item.status.toLowerCase().replace(/[^A-z0-9]+/g, '-')}` : '';
                },
                parameter: {
                    item: null,
                    getDateToString: (date: Date | string) => this.getDateToString(date)
                }
            }];
    }

    cfrStatuses: LookupValue[];
    cfrOutcomes: LookupValue[];
    isMobileFilterOpen: boolean;
    @observable()
    @cachable
    filter: string;
    @cachable
    appliedFilter: string;
    public defaultPageCount: number = 100;
    @cachable
    public myCfrsFilterSelected: boolean = false;
    @cachable
    public isPendingVetSelected: boolean = false;
    public isColumnFiltering: boolean = false;

    public getItemRoute = (id) => `/cfr/${id}?source=activities`;
    public newCfrCount: number;
    public inProgressCount: number;
    public awaitingResponseCount: number;
    public reviewRequiredCount: number;
    public closedCount: number;
    public myCfrCount: number;
    public pendingVetCount: number;
    public rightShipUser: boolean;
    public initialFilters: QueryFilter[];
    public rightshipMember: boolean = false;
    public isCustomer: boolean;

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private lookupClientService: LookupClientService, private signalClientService: SignalClientService, router: Router,
        private userClientService: UserClientService, private userClaimsService: UserClaimsService, private queryService: QueriesClientService,
        private customerService: CommonCustomerService, private entityTypeConfigurationHelper: EntityTypeConfigurationHelper) {
        super(i18N, router, TasksConstants.CFRListName, bindingEngine);
        this.companiesEntityTypes = this.entityTypeConfigurationHelper.getConfigurationByKey(["BasicCompanySearch"]);
    }

    async init() {
        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.canAccessDueDiligence = this.userClaimsService.hasPermission(CommonUiClaims.CanAccessDueDiligence);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.CfrStatuses;

        const handleStatusLookupResult = (x: LookupValueResponse) => {
            this.cfrStatuses = Enumerable.from(x.activeValues).orderBy(x => x.additionalData.statusRank)
                .where(x =>
                    x.id != CfrStatusEnum.CfrReassignment).toArray();
            this.cfrOutcomes = Enumerable.from(x.activeValues).orderBy(x => x.id)
                .where(x =>
                    x.id == CfrStatusEnum.Closed).toArray();
        }

        const result = await this.lookupClientService.getLookup(request);
        handleStatusLookupResult(result);

        const customerId = this.userClientService.currentUser.settings.customerId;
        //fallback get customerid
        if (customerId == null || customerId <= 0) {
            this.customerService.getCustomerId()
                .then(x => { this.isCustomer = (x && x > 0); })
                .catch(e => {
                    console.warn(e);
                });
        }
        else this.isCustomer = true;
        this.columns = this.getColumns();

        this.initialSorting = this.getSorting();
        if (this.applyCache()) {
            this.setItemSource();
        } else {
            this.setDefaultFilters();
        }
    }

    async activate() {
        await this.init();
        this.refreshActivityCounts();
    }

    public getSorting() {
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 3 : 2,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: this.rightShipUser ? null : false,
                isHidden: true,
                name: 'submittedDate',
                title: 'SubmittedDate'
            },
            {
                columnOrder: -1,
                sortOrder: 1,
                dataType: DataType.Number,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusRank',
                title: 'StatusRank'
            },
            {
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 2 : 3,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusDate',
                title: 'StatusDate'
            });
        return columns;
    }

    protected setDefaultFilters() {
        this.mobileSelectedFilter.push(CfrStatusEnum.New);
        this.isReviewRequiredSelected = true;
        this.mobileFilterApply(false);
    }

    private getItemSource(filters) {
        this.initialFilters = filters;
        return {
            sourceType: SourceType.ODataUrl,
            source: "odata/CfrActivities",
            columns: this.columns,
            initialFilters: filters,
            initialSorting: this.initialSorting
        };
    }

    async refreshActivityCounts() {
        if (this.userClaimsService.hasPermission(Claims.CanAccessCFRs)) {
            const x = await this.queryService.getData(`odata/CfrActivities/Genesis.GetStatusCounts()`);

            this.clearCounts();

            for (let i = 0; i < x.keys.length; i++) {

                const key = x.keys[i];
                const value = x.values[i];

                if (key == CfrStatusEnum.New) {
                    this.newCfrCount = value;
                } else if (key == CfrStatusEnum.InProgress) {
                    this.inProgressCount = value;
                } else if (key == CfrStatusEnum.AwaitingResponse) {
                    this.awaitingResponseCount = value;
                } else if (key == CfrStatusEnum.PendingReview) {
                    this.reviewRequiredCount = value;
                } else if (key == CfrStatusEnum.Closed) {
                    this.closedCount = value;
                } else if (key == -1) {
                    this.myCfrCount = value;
                } else if (key == -2) {
                    this.pendingVetCount = value;
                } else if (key == -3) {
                    this.pendingDueDiligenceCount = value;
                }
            }
        }
    }

    clearCounts() {
        this.newCfrCount = 0;
        this.inProgressCount = 0;
        this.awaitingResponseCount = 0;
        this.reviewRequiredCount = 0;
        this.closedCount = 0;
        this.myCfrCount = 0;
        this.pendingVetCount = 0;
        this.pendingDueDiligenceCount = 0;
    }

    getCount(statusId: number) {
        if (statusId == CfrStatusEnum.New && this.newCfrCount)
            return this.newCfrCount;
        if (statusId == CfrStatusEnum.InProgress && this.inProgressCount)
            return this.inProgressCount;
        if (statusId == CfrStatusEnum.AwaitingResponse && this.awaitingResponseCount)
            return this.awaitingResponseCount;
        if (statusId == CfrStatusEnum.PendingReview && this.reviewRequiredCount)
            return this.reviewRequiredCount;
        if (statusId == CfrStatusEnum.Closed && this.closedCount)
            return this.closedCount;
        if (statusId == -1 && this.myCfrCount)
            return this.myCfrCount;
        if (statusId == -2 && this.pendingVetCount)
            return this.pendingVetCount;
        if (statusId == -3 && this.pendingDueDiligenceCount)
            return this.pendingDueDiligenceCount;

        return 0;
    }

    rightshipMemberClick(isMobile: boolean = false) {
        this.rightshipMember = !this.rightshipMember;
        if (!isMobile) this.setItemSource();
    }

    myCfrsClick(isMobile: boolean = false) {
        this.myCfrsFilterSelected = !this.myCfrsFilterSelected;
        if (!isMobile) this.setItemSource();
    }

    pendingVetClick(isMobile: boolean = false) {
        this.isPendingVetSelected = !this.isPendingVetSelected;
        if (!isMobile) this.setItemSource();
    }

    private setItemSource() {

        if (!this.columns || this.columns.length <= 0) {
            this.columns = this.getColumns();
        }

        const currentFilters: QueryFilter[] = [];

        if (this.statusFilterIds && this.statusFilterIds.length > 0) {
            this.statusFilterIds.forEach(x => {
                currentFilters.push(QueryFilter.getQuery("statusId", x, "eq", DataType.Number, false, false, "statusGroup", true));
            });
        }

        if (this.isReviewRequiredSelected) {
            currentFilters.push(QueryFilter.getQuery("statusId", CfrStatusEnum.PendingReview, "eq", DataType.Number, false, false, "statusGroup", true));
        }

        if (this.myCfrsFilterSelected) {
            currentFilters.push(QueryFilter.getQuery("reviewerId", this.userClientService.currentUser.id, "eq", DataType.Number, true));
            currentFilters.push(QueryFilter.getQuery("statusId", [
                CfrStatusEnum.New,
                CfrStatusEnum.InProgress,
                CfrStatusEnum.AwaitingResponse,
                CfrStatusEnum.PendingReview,
                CfrStatusEnum.CfrReassignment,
            ], "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }

        if (this.isPendingVetSelected) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("hasVet", this.isPendingVetSelected, "eq", DataType.Boolean, true, false, guid, true));
        }

        if (this.isPendingDueDiligence && this.dueDiligenceFilterVisible) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("hasDueDiligence", this.isPendingDueDiligence, "eq", DataType.Boolean, true, false, guid, true));
        }

        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("vesselName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("cfrIdStr", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        if (this.rightshipMember) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("isRightShipMember", true, "eq", DataType.Boolean, true, false, guid, true));
        }

        if (this.categoryFilterIds.length > 0) {
            currentFilters.push(QueryFilter.getQuery("cfrCategoryId", this.categoryFilterIds, "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }
        
        //#region doc company
        if (this.docCompanyFilterActive) {

            const docCompanyFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "docCompanyId",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedDocCompanies || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            currentFilters.push(docCompanyFilter);
        }
        //#endregion

        //#region dwt
        if (this.dwtFirstFilter || this.dwtSecondFilter) {
            currentFilters.push(QueryFilter.getQuery('dwt', this.dwtFirstFilter ? this.dwtFirstFilter : 0, 'ge', DataType.Number, true));
            currentFilters.push(QueryFilter.getQuery('dwt', this.dwtSecondFilter ? this.dwtSecondFilter : 0, 'le', DataType.Number, true));
        }
        //#endregion

        this.itemSource = this.getItemSource(currentFilters);
        this.refreshActivityCounts();
    }

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    clearFilter() {
        this.filter = "";
        this.appliedFilter = this.filter;
        this.setItemSource();
    }

    attached() {
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Cfr), this.refreshCfrStatus);
    }

    detached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Cfr), this.refreshCfrStatus);
        super.detached();
    }

    refreshCfrStatus = async (param: EntityStatusChangeModel) => {
        if (this._isReloading) {
            console.log('refreshCfrStatus is already in progress, skipping...');
            return;
        }

        try {
            if (this.dataGridVM) {
                this._isReloading = true;
                if (this.dataGridVM.page == 0) {
                    await this.dataGridVM.reloadData();
                } else {
                    const item = this.dataGridVM.itemsCollection.filter(x => x.item.cfrId == param.entityId)[0];
                    if (item) {
                        item.item.statusId = param.statusId;
                        item.item.status = this.cfrStatuses.filter(x => x.id == param.statusId).map(x => x.name)[0];
                    }
                }
                await this.refreshActivityCounts();
            }
        }
        finally {
            this._isReloading = false;
        }
    }

    @computedFrom(Reflection.getPropertyPath<CfrActivities>(z => z.showStatusFilter),
        Reflection.getPropertyPath<CfrActivities>(z => z.showColumnSorting),
        Reflection.getPropertyPath<CfrActivities>(z => z.showCategoryFilter),
        Reflection.getPropertyPath<CfrActivities>(z => z.showDocCompanyFilter),
        Reflection.getPropertyPath<CfrActivities>(z => z.showDwtFilter))
    public get isSubFilterOpen(): boolean {
        return this.showStatusFilter || this.showColumnSorting || this.showCategoryFilter || this.showDocCompanyFilter || this.showDwtFilter;
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }
        this.showStatusFilter = false;
        this.showColumnSorting = false;
        this.showCategoryFilter = false;
        this.showDocCompanyFilter = false;
        this.showDwtFilter = false;
    }

    hideMobileFilters() {
        this.isMobileFilterOpen = false;
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        this.appliedFilter = this.filter;
        if (this.columns.length > 0) {
            const column = this.columns.filter(x => x.name == 'status')[0];
            column.filters = [];
        }

        if ((!this.statusFilterIds || this.statusFilterIds.length == 0) && this.mobileSelectedFilter && this.mobileSelectedFilter.length > 0) {
            this.statusFilterIds = CloneHelper.cloneObject(this.mobileSelectedFilter);
        }

        this.setItemSource();
    }

    clearAllFilters($event, reload: boolean = true) {
        if ($event)
            $event.stopPropagation();
        if (this.dataGridVM) {
            this.dataGridVM.clearSorting();
            this.isColumnFiltering = false
        }
        this.appliedFilter = null;
        this.filter = null;
        this.mobileSelectedFilter = [];
        this.statusFilterIds = [];
        this.myCfrsFilterSelected = false;
        this.rightshipMember = false;
        this.isReviewRequiredSelected = false;
        this.isPendingVetSelected = false;
        this.categoryFilterIds = [];
        this.selectedDocCompanies = [];
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.dwtIsApplied = false;
        this.isPendingDueDiligence = false;

        this.closeAllFilters();

        if (reload)
            this.setItemSource();
    }

    @computedFrom(Reflection.getPropertyPath<CfrActivities>(z => z.showStatusFilter),
        Reflection.getPropertyPath<CfrActivities>(z => z.showColumnSorting),
        Reflection.getPropertyPath<CfrActivities>(z => z.showCategoryFilter),
        Reflection.getPropertyPath<CfrActivities>(z => z.showDocCompanyFilter),
        Reflection.getPropertyPath<CfrActivities>(z => z.showDwtFilter))
    public get mobileFilterSelected() {
        return this.showStatusFilter || this.showColumnSorting || this.showCategoryFilter || this.showDocCompanyFilter || this.showDwtFilter;
    }

    public closeAllFilters() {
        this.showStatusFilter = false;
        this.showColumnSorting = false;
        this.showCategoryFilter = false;
        this.showDocCompanyFilter = false;
        this.showDwtFilter = false;
        return false;
    }

    public selectedFilter: { title: string, clear: () => void, apply: () => void };

    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }

    @computedFrom("userClientService.currentUser.id")
    get buParams() {
        const canViewAll = this.userClaimsService.hasPermission(Claims.RightShipUser);
        const customerId = this.userClientService.currentUser.settings.customerId ? this.userClientService.currentUser.settings.customerId : 'null';

        return customerId + ',' + (canViewAll ? 'null' : this.userClientService.currentUser.id);
    }

    //#region StatusFilter
    public showStatusFilter: boolean = false;
    @cachable
    public statusFilterIds: number[] = [];
    @cachable
    public mobileSelectedFilter: number[] = [];
    public statusDescription: string = "";

    toggleStatusFilters() {
        if (!this.showStatusFilter) {
            this.closeAllFilters();
        }

        this.showStatusFilter = !this.showStatusFilter;

        this.selectedFilter = {
            title: "Status",
            clear: () => {
                this.clearStatusFilter();
            },
            apply: () => {
                this.onStatusFilter(this.mobileSelectedFilter.map(id => {
                    return { id: id };
                }), false);
            }
        }
    }

    onStatusFilter(items: any[], reload: boolean = true) {
        this.statusFilterIds = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.showStatusFilter = false;
    }

    @computedFrom('statusFilterIds', 'statusFilterIds.length')
    public get statusFilterCount() {
        return this.statusFilterIds ? this.statusFilterIds.length : 0;
    }

    public clearStatusFilter() {
        this.statusFilterIds = [];
        this.mobileSelectedFilter = [];
        this.setItemSource();
    }

    public getExcludedStatuses(): number[] {
        return [
            CfrStatusEnum.CfrReassignment,
            CfrStatusEnum.PendingReview
        ];
    }
    //#endregion

    //#region review Required
    @cachable
    public isReviewRequiredSelected: boolean = false;

    reviewRequiredClick(isMobile: boolean = false) {
        this.isReviewRequiredSelected = !this.isReviewRequiredSelected;
        if (!isMobile) this.setItemSource();
    }
    //#endregion

    //#region category
    public showCategoryFilter: boolean = false;
    @cachable
    public categoryFilterIds: number[] = [];
    public categoryDescription: string = "";

    toggleCategoryFilters() {
        if (!this.showCategoryFilter) {
            this.closeAllFilters();
        }
        this.showCategoryFilter = !this.showCategoryFilter;
        this.selectedFilter = {
            title: "Category",
            clear: () => {
                this.clearCategoryFilters();
            },
            apply: () => {
                this.setItemSource();
                this.showCategoryFilter = false;
            }
        }
    }

    onCategoryFilter(event, reload: boolean = true) {
        this.categoryFilterIds = event.filters.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.showCategoryFilter = false;
    }

    clearCategoryFilters() {
        this.categoryFilterIds = [];
        this.setItemSource();
    }

    @computedFrom("categoryFilterIds", "categoryFilterIds.length")
    public get categoryFilterActive() {
        return this.categoryFilterIds?.length > 0;
    }
    //#endregion

    //#region column sorting 

    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];

    public toggleColumnSortingFilters() {
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
                this.clearStatusFilter();
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleColumnSortingFilters();
    }

    sortByMobile(column: DataGridColumn) {
        this.dataGridVM.sortingTriggered(column);
        this.isColumnFiltering = this.dataGridVM.visibleColumns.some(item => item.isAscending === true || item.isAscending === false);
        this.setItemSource();
    }

    sortClass(column: DataGridColumn) {
        if (column.isAscending) {
            return "Ascending"
        }
        else if (column.isAscending == false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    //#endregion

    //#region doc company filter
    public showDocCompanyFilter: boolean = false;
    @cachable
    public selectedDocCompanies: number[] = [];

    public toggleDocCompanyFilters() {
        if (!this.showDocCompanyFilter) {
            this.closeAllFilters();
        }
        this.showDocCompanyFilter = !this.showDocCompanyFilter;
    }

    public applyDocCompanyFilter(items: any[], reload: boolean = true) {
        if (!items) {
            return;
        }
        this.selectedDocCompanies = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleDocCompanyFilters();
    }

    public clearDocCompanyFilter() {
        this.selectedDocCompanies = [];
        this.setItemSource();
    }

    @computedFrom("selectedDocCompanies", "selectedDocCompanies.length")
    public get docCompanyFilterActive() {
        return this.selectedDocCompanies?.length > 0 && this.selectedDocCompanies[0] != null;
    }

    //#endregion

    //#region dwt filter
    @cachable
    public dwtFirstFilter: number;
    @cachable
    public dwtSecondFilter: number;
    public dwtDefaults: { low: number, high: number } = { low: 0, high: 565000 };
    public showDwtFilter: boolean = false;
    public dwtIsApplied: boolean = false;

    public toggleDwtFilters() {
        if (!this.showDwtFilter) {
            this.closeAllFilters();
        }
        this.showDwtFilter = !this.showDwtFilter;
    }

    public applyDwtFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.dwtFirstFilter = filterValue.low;
            this.dwtSecondFilter = filterValue.high;
            this.dwtIsApplied = true;
        }

        this.setItemSource();
        this.toggleDwtFilters();
    }

    public clearDwtFilter() {
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.dwtIsApplied = false;
        this.setItemSource();
    }

    @computedFrom("dwtIsApplied", "dwtFirstFilter", "dwtSecondFilter")
    public get dwtFilterActive() {
        return this.dwtIsApplied && ((this.dwtFirstFilter && this.dwtFirstFilter >= this.dwtDefaults.low) || (this.dwtSecondFilter && this.dwtSecondFilter <= this.dwtDefaults.high));
    }

    //#endregion

    //#region due diligence filter
    
    public pendingDueDiligenceCount: number = 0;
    public canAccessDueDiligence: boolean;
    @cachable
    public isPendingDueDiligence: boolean;
    
    @computedFrom(Reflection.getPropertyPath<CfrActivities>(z => z.canAccessDueDiligence),
        Reflection.getPropertyPath<CfrActivities>(z => z.rightShipUser))
    public get dueDiligenceFilterVisible(): boolean
    {
        return this.canAccessDueDiligence && this.rightShipUser;
    }

    pendingDueDiligenceClick(isMobile: boolean = false) {
        this.isPendingDueDiligence = !this.isPendingDueDiligence;
        if (!isMobile) this.setItemSource();
    }

    //#endregion
}